import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 用于防止自动监听器和手动登录逻辑冲突的锁
  const isLoggingInRef = useRef(false);

  // 1. 初始化监听器
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // 如果正在进行手动登录，直接跳过，由 login 函数自己处理
      if (isLoggingInRef.current) {
        return;
      }

      if (firebaseUser) {
        try {
          const response = await apiService.getProfile();
          const userData = response.user || response;
          
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            ...userData
          });
        } catch (error) {
          console.error('Error fetching user profile (auto-auth):', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const register = async (userData) => {
    try {
      const response = await apiService.register(userData);
      //return { success: true, message: 'Registration successful! Awaiting admin approval.' };
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    isLoggingInRef.current = true;
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const idToken = await firebaseUser.getIdToken(true);
      const response = await apiService.login(idToken);
      
      const userData = response.user || response;
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        ...userData
      });
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      await signOut(auth);
      throw error;
    } finally {
      setTimeout(() => {
        isLoggingInRef.current = false;
      }, 500);
    }
  };

  const logout = async () => {
    try {
      try {
        await apiService.request('/auth/logout', { method: 'POST' });
      } catch (e) {
        console.warn('Backend logout notification failed', e);
      }
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error(error.message);
    }
  };

  // 2. 更新用户资料 (推送到服务器)
  // 适用于：修改个人设置、头像等需要写入的操作
  const updateUser = async (updates) => {
    if (!user) return;
    try {
      await apiService.updateProfile(updates);
      setUser(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Update user error:', error);
      throw new Error(error.message);
    }
  };

  // 🟢 3. [新增] 重新加载用户资料 (从服务器拉取)
  // 适用于：课程结算、徽章获得、商店购买后，同步最新的积分和状态
  const reloadUser = async () => {
    if (!auth.currentUser) return;
    try {
      // 这里的 getProfile 会获取数据库最新的 points, badges, streak 等
      const response = await apiService.getProfile();
      const userData = response.user || response;
      
      setUser(prev => {
        if (!prev) return null;
        return {
          ...prev, // 保留原有的 Firebase uid/email
          ...userData // 覆盖最新的数据库字段
        };
      });
      return userData;
    } catch (error) {
      console.error('Failed to reload user:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateUser, // 用于【写】数据
    reloadUser  // 🟢 用于【读】最新数据 (同步)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;